import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const EmotePicker = ({ isOpen, onClose, onEmoteSelect }) => {
  const [globalEmotes, setGlobalEmotes] = useState([]);
  const [customEmotes, setCustomEmotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('global'); // 'global' or 'custom'

  useEffect(() => {
    if (isOpen) {
      fetchEmotes();
    }
    const onChange = () => { if (isOpen) fetchEmotes(); };
    window.addEventListener('beatseek:custom-emotes-changed', onChange);
    return () => window.removeEventListener('beatseek:custom-emotes-changed', onChange);
  }, [isOpen]);

  const fetchEmotes = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('beatseek_token');
      
      // Fetch global emotes
      const globalResponse = await fetch(`${BACKEND_URL}/api/emotes/global`);
      if (globalResponse.ok) {
        const global = await globalResponse.json();
        console.log('📦 Loaded global emotes:', global);
        setGlobalEmotes(global);
      }
      
      // Fetch user's custom emotes (if logged in)
      if (token) {
        const customResponse = await fetch(`${BACKEND_URL}/api/emotes/my-custom`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (customResponse.ok) {
          const custom = await customResponse.json();
          console.log('✨ Loaded custom emotes:', custom);
          setCustomEmotes(custom);
        }
      }
    } catch (error) {
      console.error('Error fetching emotes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEmoteClick = (emote) => {
    console.log('🎯 Emote clicked:', emote);
    onEmoteSelect(`:${emote.name}:`);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[9999]"
      onClick={(e) => {
        // Close only if clicking the overlay backdrop
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div 
        className="bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 rounded-2xl border border-purple-500/20 w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-purple-700/50">
          <div className="flex items-center gap-3">
            <span className="text-2xl">😊</span>
            <div>
              <h3 className="text-lg font-semibold text-white">Emote Picker</h3>
              <p className="text-xs text-gray-400">Click an emote to add it to your message</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors duration-200"
          >
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-purple-700/50 bg-gray-900/50">
          <button
            onClick={() => setActiveTab('global')}
            className={`flex-1 px-4 py-2 text-sm font-medium transition-all duration-200 ${
              activeTab === 'global'
                ? 'text-white bg-purple-600/20 border-b-2 border-purple-500'
                : 'text-gray-400 hover:text-white hover:bg-purple-600/10'
            }`}
          >
            🌍 Global ({globalEmotes.length})
          </button>
          <button
            onClick={() => setActiveTab('custom')}
            className={`flex-1 px-4 py-2 text-sm font-medium transition-all duration-200 ${
              activeTab === 'custom'
                ? 'text-white bg-purple-600/20 border-b-2 border-purple-500'
                : 'text-gray-400 hover:text-white hover:bg-purple-600/10'
            }`}
          >
            ✨ My Custom ({customEmotes.length})
          </button>
        </div>

        {/* Emote Grid */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
              <span className="ml-3 text-gray-400">Loading emotes...</span>
            </div>
          ) : (
            <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
              {activeTab === 'global' ? (
                globalEmotes.length > 0 ? (
                  globalEmotes.map((emote) => {
                    console.log('🎨 Rendering global emote:', emote);
                    return (
                      <button
                        key={emote.id}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleEmoteClick(emote);
                        }}
                        className="group relative bg-gray-800/50 hover:bg-purple-600/30 border border-gray-700/50 hover:border-purple-500/50 rounded-lg p-3 transition-all duration-200 hover:scale-110 flex items-center justify-center min-h-[60px]"
                        title={`:${emote.name}:`}
                      >
                        {emote.emoji ? (
                          <span className="text-3xl leading-none" style={{ display: 'block' }}>
                            {emote.emoji}
                          </span>
                        ) : (
                          <img 
                            src={`${BACKEND_URL}/api${emote.file_path_56}`} 
                            alt={emote.name}
                            className="w-12 h-12 object-contain"
                            onError={(e) => {
                              console.error('Failed to load emote:', emote.name, emote.file_path_56);
                              e.target.style.display = 'none';
                            }}
                            onLoad={() => console.log('✅ Loaded emote image:', emote.name)}
                          />
                        )}
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                          :{emote.name}:
                        </div>
                      </button>
                    );
                  })
                ) : (
                  <div className="col-span-full text-center py-8 text-gray-400">
                    No global emotes available
                  </div>
                )
              ) : (
                customEmotes.length > 0 ? (
                  customEmotes.map((emote) => (
                    <button
                      key={emote.id}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleEmoteClick(emote);
                      }}
                      className="group relative bg-gray-800/50 hover:bg-purple-600/30 border border-gray-700/50 hover:border-purple-500/50 rounded-lg p-3 transition-all duration-200 hover:scale-110 flex items-center justify-center"
                      title={`:${emote.name}:`}
                    >
                      <img 
                        src={`${BACKEND_URL}/api${emote.file_path_56}`} 
                        alt={emote.name}
                        className="w-12 h-12 object-contain"
                        onError={(e) => {
                          console.error('Failed to load custom emote:', emote.name, emote.file_path_56);
                          e.target.style.display = 'none';
                        }}
                      />
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                        :{emote.name}:
                      </div>
                    </button>
                  ))
                ) : (
                  <div className="col-span-full text-center py-8 text-gray-400">
                    <p className="mb-2">You don&apos;t have any custom emotes yet</p>
                    <p className="text-xs text-gray-500">Upload custom emotes from your profile</p>
                  </div>
                )
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-purple-700/50 bg-gray-900/50">
          <p className="text-xs text-center text-gray-400">
            💡 Tip: You can also type <span className="text-purple-400 font-mono">:emotename:</span> directly in your message
          </p>
        </div>
      </div>
    </div>
  );
};

export default EmotePicker;
